import { allCountries } from 'country-telephone-data';

/** Prefer these ISO2 codes when multiple territories share the same dial code (e.g. +1). */
const ISO2_AMBIGUITY_PREFERENCE = ['us', 'in', 'gb'];

export function iso2ToFlagEmoji(iso2: string): string {
  if (!iso2 || iso2.length !== 2) {
    return '';
  }
  const upper = iso2.toUpperCase();
  return String.fromCodePoint(
    ...upper.split('').map((c) => 127397 + c.charCodeAt(0))
  );
}

export function normalizeCountryCode(code: string | undefined | null): string {
  if (code == null || code === '') {
    return '';
  }
  const t = String(code).trim();
  const digits = (t.startsWith('+') ? t.slice(1) : t).replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

export function dialCodeToIso2(dial: string): string | '' {
  const normalized = normalizeCountryCode(dial);
  if (!normalized) {
    return '';
  }
  const matches = allCountries.filter(
    (c) => `+${c.dialCode}` === normalized
  );
  if (matches.length === 0) {
    return '';
  }
  if (matches.length === 1) {
    return matches[0].iso2;
  }
  const preferred = ISO2_AMBIGUITY_PREFERENCE.map((iso) =>
    matches.find((m) => m.iso2 === iso)
  ).find(Boolean);
  if (preferred) {
    return preferred.iso2;
  }
  return [...matches].sort((a, b) => a.name.localeCompare(b.name))[0].iso2;
}

export type CountryDialOption = {
  iso2: string;
  dialCode: string;
  label: string;
};

let cachedSortedOptions: CountryDialOption[] | undefined;

export function getSortedCountryDialOptions(): CountryDialOption[] {
  if (cachedSortedOptions) {
    return cachedSortedOptions;
  }
  if (!Array.isArray(allCountries)) {
    return [];
  }
  cachedSortedOptions = [...allCountries]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({
      iso2: c.iso2,
      dialCode: c.dialCode,
      label: `${iso2ToFlagEmoji(c.iso2)} +${c.dialCode} ${c.name}`,
    }));
  return cachedSortedOptions;
}

/** Ensures MUI Select `value` always matches a MenuItem (avoids out-of-range runtime errors). */
export function resolveCountrySelectIso(
  fieldValue: unknown,
  options: CountryDialOption[]
): string {
  if (options.length === 0) {
    return '';
  }
  const normalized = normalizeCountryCode(fieldValue as string);
  const fromDial = normalized ? dialCodeToIso2(normalized) : '';
  if (fromDial && options.some((o) => o.iso2 === fromDial)) {
    return fromDial;
  }
  if (options.some((o) => o.iso2 === 'in')) {
    return 'in';
  }
  return options[0].iso2;
}
